import React, { useState } from "react";

import { ifNotResolvedImmediately } from "@examspark/common/dist/utils";

import { colors } from "../../themes";
import { ConfirmableProps, confirm } from "../../utils/confirmable";

import Modal, { ModalProps } from "./Modal";
import SubmitButtons from "./SubmitButtons";

type ConfirmableChildren<TResult> = React.FC<
  React.PropsWithChildren<
    ConfirmableProps<TResult> & { isResolving: boolean; result: TResult }
  >
>;

type Props<TResult> = React.PropsWithChildren<
  Omit<ModalProps, "isOpen" | "buttons"> & {
    onResolving?: (result: TResult) => Promise<void> | void;
    defaultResult: TResult;
    buttons?: ConfirmableChildren<TResult>;
    contents?: ConfirmableChildren<TResult>;
  }
>;

type DefaultProps = Omit<ModalProps, "isOpen" | "buttons"> & {
  onResolving?: (result: boolean) => Promise<void> | void;
  confirmLabel?: string;
  confirmColor?: colors;
  cancelLabel?: string;
};

const GenericConfirmModal = <TResult,>({
  onResolve,
  onResolving,
  defaultResult,
  buttons,
  contents,
  children,
  ...modalProps
}: Props<TResult> & ConfirmableProps<TResult>) => {
  const [isResolving, setIsResolving] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [result, setResult] = useState(defaultResult);

  const onResolveInternal = async (r: TResult) => {
    if (isResolving) return;

    setResult(r);

    if (onResolving) {
      const resolvingPromise = onResolving(r);
      if (resolvingPromise) {
        await ifNotResolvedImmediately(resolvingPromise, () =>
          setIsResolving(true),
        );
      }
    }

    setIsOpen(false);
  };

  const onAfterClose = () => onResolve(result);

  const childProps = { onResolve: onResolveInternal, isResolving, result };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => onResolveInternal(defaultResult)}
      onAfterClose={onAfterClose}
      disabled={isResolving}
      buttons={buttons?.(childProps)}
      {...modalProps}
    >
      {contents?.(childProps)}
      {children}
    </Modal>
  );
};

const ConfirmModal: React.FC<
  React.PropsWithChildren<DefaultProps & ConfirmableProps<boolean>>
> & {
  show: (props?: DefaultProps) => Promise<boolean>;
  Generic: typeof GenericConfirmModal;
} = ({ confirmLabel, confirmColor, cancelLabel, children, ...modalProps }) => (
  <GenericConfirmModal<boolean>
    buttons={({ onResolve, isResolving, result }) => (
      <SubmitButtons
        submitLabel={confirmLabel || "Confirm"}
        color={confirmColor}
        cancelLabel={cancelLabel}
        isSubmitting={isResolving && result}
        isCancelling={isResolving && !result}
        onSubmit={() => onResolve(true)}
        onCancel={() => onResolve(false)}
      />
    )}
    defaultResult={false}
    title="Confirm"
    {...modalProps}
  >
    {children}
  </GenericConfirmModal>
);

ConfirmModal.show = (props) => confirm(ConfirmModal, props || {});
ConfirmModal.Generic = GenericConfirmModal;

export default ConfirmModal;
